import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * HostEditModal — create + edit an ai_hosts row (P1-AI07).
 *
 * Same shape as ScheduledTasksPage's edit modal: one form, two modes
 * ('create' vs 'edit'). The submit handler in the parent decides
 * between POST and PATCH from the mode prop. Server-side ownership
 * flip on PATCH for a deploy_script_managed=true row is surfaced as
 * an info banner above the form so the operator understands what
 * saving will do.
 *
 * Validation surface mirrors the server validators (validateRegisterHost
 * + validateUpdateHostPatch in core/ai_hosts/validators.ts):
 *   - name immutable post-create (input disabled in edit mode)
 *   - name matches HOST_NAME_RE
 *   - os required + non-empty
 *   - status is one of active/draining/archived (dropdown)
 *   - capabilities entered as comma- or newline-separated text
 *   - all nullable strings clear on empty + space-trimmed
 */
import { useMemo } from 'react';
import { Field, fieldClass } from './shared.js';
const HOST_NAME_RE = /^[a-z0-9][a-z0-9._-]{1,99}$/i;
export function emptyHostDraft() {
    return {
        name: '',
        os: 'mac',
        tailscaleName: '',
        tailscaleIp: '',
        sshUser: '',
        capabilitiesText: '',
        baseUrlOllama: '',
        status: 'active',
        notes: '',
    };
}
export function hostToDraft(h) {
    return {
        name: h.name,
        os: h.os,
        tailscaleName: h.tailscaleName ?? '',
        tailscaleIp: h.tailscaleIp ?? '',
        sshUser: h.sshUser ?? '',
        capabilitiesText: (h.capabilities ?? []).join(', '),
        baseUrlOllama: h.baseUrlOllama ?? '',
        status: h.status,
        notes: h.notes ?? '',
    };
}
export function parseCapabilities(text) {
    if (!text.trim())
        return [];
    return text
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}
export function isValidHostName(name) {
    return HOST_NAME_RE.test(name);
}
/**
 * The form fields themselves, without any modal chrome. Rendered by
 * HostEditModal (create) and by HostDetailDrawer's Settings tab (edit)
 * so a host is edited in the same place it is managed — the row no
 * longer carries its own Edit button (2026-09-04).
 */
export function HostEditFields({ mode, original, draft, fieldErrors, generalErrors, formBanner, onChange, }) {
    const nameValid = useMemo(() => draft.name === '' || isValidHostName(draft.name.trim()), [draft.name]);
    const ownershipNote = mode === 'edit' && original?.deployScriptManaged ? (_jsxs("div", { className: "alert alert-info small mb-3", children: ["This host is currently owned by the ", _jsx("strong", { children: "deploy script" }), ". Saving any change will flip ownership to ", _jsx("strong", { children: "ui" }), " \u2014 the deploy script will skip this row on its next re-deploy."] })) : null;
    return (_jsxs(_Fragment, { children: [ownershipNote, formBanner && (_jsx("div", { className: "alert alert-danger", children: formBanner })), generalErrors.length > 0 && (_jsxs("div", { className: "alert alert-warning", children: [_jsx("div", { className: "fw-semibold mb-1", children: "Server validation errors:" }), _jsx("ul", { className: "mb-0 small", children: generalErrors.map((m, i) => (_jsx("li", { children: m }, i))) })] })), _jsxs(Field, { label: "Name", required: true, errors: fieldErrors.name, hint: "Stable identifier. Lowercase, 2-100 chars, [a-z0-9._-]. Immutable after create.", children: [_jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.name ?? (!nameValid ? ['invalid'] : undefined)), value: draft.name, onChange: (e) => onChange('name', e.target.value), autoComplete: "off", spellCheck: false, disabled: mode === 'edit', maxLength: 100, pattern: HOST_NAME_RE.source }), !nameValid && !fieldErrors.name && (_jsxs("div", { className: "invalid-feedback d-block small", children: ["Must match ", HOST_NAME_RE.source, "."] }))] }), _jsx(Field, { label: "OS", required: true, errors: fieldErrors.os, hint: "One of: mac, windows, linux. Lowercase.", children: _jsxs("select", { className: fieldClass('form-select', fieldErrors.os), value: draft.os, onChange: (e) => onChange('os', e.target.value), children: [_jsx("option", { value: "mac", children: "mac" }), _jsx("option", { value: "windows", children: "windows" }), _jsx("option", { value: "linux", children: "linux" })] }) }), _jsxs("div", { className: "row g-2", children: [_jsx("div", { className: "col-md-6", children: _jsx(Field, { label: "Tailscale name", errors: fieldErrors.tailscaleName, hint: "e.g. erwins-macbook-pro-2", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.tailscaleName), value: draft.tailscaleName, onChange: (e) => onChange('tailscaleName', e.target.value), autoComplete: "off", spellCheck: false }) }) }), _jsx("div", { className: "col-md-6", children: _jsx(Field, { label: "Tailscale IP", errors: fieldErrors.tailscaleIp, hint: "e.g. 100.85.145.120", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.tailscaleIp), value: draft.tailscaleIp, onChange: (e) => onChange('tailscaleIp', e.target.value), autoComplete: "off", spellCheck: false }) }) })] }), _jsx(Field, { label: "SSH user", errors: fieldErrors.sshUser, hint: "Optional. Login used for install / provision SSH dispatch.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.sshUser), value: draft.sshUser, onChange: (e) => onChange('sshUser', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Capabilities", errors: fieldErrors.capabilitiesText, hint: "Comma- or newline-separated. Common: ollama-host, cli-host.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.capabilitiesText), value: draft.capabilitiesText, onChange: (e) => onChange('capabilitiesText', e.target.value), autoComplete: "off", spellCheck: false, placeholder: "ollama-host, cli-host" }) }), _jsx(Field, { label: "Ollama base URL", errors: fieldErrors.baseUrlOllama, hint: "HTTP base for the host's Ollama daemon. Empty until Ollama is installed.", children: _jsx("input", { type: "url", className: fieldClass('form-control', fieldErrors.baseUrlOllama), value: draft.baseUrlOllama, onChange: (e) => onChange('baseUrlOllama', e.target.value), autoComplete: "off", spellCheck: false, placeholder: "http://erwins-macbook-pro-2:11434" }) }), _jsx(Field, { label: "Status", errors: fieldErrors.status, hint: "active = healthy; draining = no new work; archived = soft-deleted.", children: _jsxs("select", { className: fieldClass('form-select', fieldErrors.status), value: draft.status, onChange: (e) => onChange('status', e.target.value), children: [_jsx("option", { value: "active", children: "active" }), _jsx("option", { value: "draining", children: "draining" }), _jsx("option", { value: "archived", children: "archived" })] }) }), _jsx(Field, { label: "Notes", errors: fieldErrors.notes, hint: "Optional. Free-form operator notes.", children: _jsx("textarea", { className: fieldClass('form-control', fieldErrors.notes), rows: 2, value: draft.notes, onChange: (e) => onChange('notes', e.target.value), spellCheck: true }) })] }));
}
export function HostEditModal({ mode, original, draft, fieldErrors, generalErrors, formBanner, submitting, onChange, onCancel, onSubmit, }) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal show", style: { display: 'block' }, role: "dialog", "aria-modal": "true", children: _jsx("div", { className: "modal-dialog modal-dialog-centered modal-lg", children: _jsx("div", { className: "modal-content", children: _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: mode === 'create' ? 'Register AI host' : 'Edit AI host' }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onCancel, disabled: submitting })] }), _jsx("div", { className: "modal-body", children: _jsx(HostEditFields, { mode: mode, original: original, draft: draft, fieldErrors: fieldErrors, generalErrors: generalErrors, formBanner: formBanner, onChange: onChange }) }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onCancel, disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: submitting, children: submitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Saving\u2026"] })) : mode === 'create' ? ('Register host') : ('Save') })] })] }) }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
export function draftToCreate(d) {
    const out = {
        name: d.name.trim(),
        os: d.os.trim(),
        capabilities: parseCapabilities(d.capabilitiesText),
        status: d.status,
    };
    if (d.tailscaleName.trim())
        out.tailscaleName = d.tailscaleName.trim();
    if (d.tailscaleIp.trim())
        out.tailscaleIp = d.tailscaleIp.trim();
    if (d.sshUser.trim())
        out.sshUser = d.sshUser.trim();
    if (d.baseUrlOllama.trim())
        out.baseUrlOllama = d.baseUrlOllama.trim();
    if (d.notes.trim())
        out.notes = d.notes.trim();
    return out;
}
export function draftToPatch(d, orig) {
    const patch = {};
    if (d.os.trim() !== orig.os)
        patch.os = d.os.trim();
    const tn = d.tailscaleName.trim() || null;
    if (tn !== orig.tailscaleName)
        patch.tailscaleName = tn;
    const ti = d.tailscaleIp.trim() || null;
    if (ti !== orig.tailscaleIp)
        patch.tailscaleIp = ti;
    const su = d.sshUser.trim() || null;
    if (su !== orig.sshUser)
        patch.sshUser = su;
    const caps = parseCapabilities(d.capabilitiesText);
    if (caps.join(',') !== (orig.capabilities ?? []).join(',')) {
        patch.capabilities = caps;
    }
    const bu = d.baseUrlOllama.trim() || null;
    if (bu !== orig.baseUrlOllama)
        patch.baseUrlOllama = bu;
    if (d.status !== orig.status)
        patch.status = d.status;
    const nt = d.notes.trim() || null;
    if (nt !== orig.notes)
        patch.notes = nt;
    return patch;
}
export function errorFieldKey(rawField) {
    if (!rawField)
        return null;
    const f = rawField.replace(/^\/+/, '').split(/[./]/).filter(Boolean).pop();
    switch (f) {
        case 'name':
            return 'name';
        case 'os':
            return 'os';
        case 'tailscale_name':
        case 'tailscaleName':
            return 'tailscaleName';
        case 'tailscale_ip':
        case 'tailscaleIp':
            return 'tailscaleIp';
        case 'ssh_user':
        case 'sshUser':
            return 'sshUser';
        case 'capabilities':
            return 'capabilitiesText';
        case 'base_url_ollama':
        case 'baseUrlOllama':
            return 'baseUrlOllama';
        case 'status':
            return 'status';
        case 'notes':
            return 'notes';
        default:
            return null;
    }
}
//# sourceMappingURL=HostEditModal.js.map